import { z } from 'zod';
import { contentTemplateItemSchema } from './content-template';
import { expireRuleItemSchema } from './expire-rule';
import { messageTemplateNotesSchema } from './operation-sender';
import { processActionItemSchema } from './process-action';
import {
  autoScheduledMessageKindSchema,
  sentTimeRuleItemSchema,
} from './sent-time-rule';

export const messageProviderValues = ['whatsapp'] as const;
export const messageProviderSchema = z.enum(messageProviderValues);
export type MessageProvider = z.infer<typeof messageProviderSchema>;

export const messageTemplateItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  messageProvider: messageProviderSchema,
  createdOn: z.string(),
  modifiedOn: z.string().nullable(),
  expireRuleId: z.uuid().nullable(),
  sentTimeRuleId: z.uuid().nullable(),
  contentTemplateId: z.uuid().nullable(),
  processesActionId: z.uuid().nullable(),
  expireRule: expireRuleItemSchema.nullable().optional(),
  sentTimeRule: sentTimeRuleItemSchema.nullable().optional(),
  contentTemplate: contentTemplateItemSchema.nullable().optional(),
  processAction: processActionItemSchema.nullable().optional(),
  /** Which automated message the scheduler sends with this template, if any. */
  autoScheduled: autoScheduledMessageKindSchema.nullable().optional(),
});
export type MessageTemplateItem = z.infer<typeof messageTemplateItemSchema>;

export const createMessageTemplateInput = z.object({
  title: z.string().min(1),
  messageProvider: messageProviderSchema.optional(),
  expireRuleId: z.uuid().nullable().optional(),
  sentTimeRuleId: z.uuid().nullable().optional(),
  contentTemplateId: z.uuid().nullable().optional(),
  processesActionId: z.uuid().nullable().optional(),
});
export type CreateMessageTemplateInput = z.infer<
  typeof createMessageTemplateInput
>;

export const updateMessageTemplateInput = z.object({
  id: z.uuid(),
  title: z.string().min(1).optional(),
  expireRuleId: z.uuid().nullable().optional(),
  sentTimeRuleId: z.uuid().nullable().optional(),
  contentTemplateId: z.uuid().nullable().optional(),
  processesActionId: z.uuid().nullable().optional(),
});
export type UpdateMessageTemplateInput = z.infer<
  typeof updateMessageTemplateInput
>;

export const findMessageTemplatesInput = z.object({
  offset: z.number().int().nonnegative().optional(),
  limit: z.number().int().min(1).max(200).optional(),
  title: z.string().optional(),
  sort: z.enum(['title', 'createdOn']).optional(),
  order: z.enum(['asc', 'desc']).optional(),
});
export type FindMessageTemplatesInput = z.infer<
  typeof findMessageTemplatesInput
>;

export const findMessageTemplatesResultSchema = z.object({
  data: z.array(messageTemplateItemSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});
export type FindMessageTemplatesResult = z.infer<
  typeof findMessageTemplatesResultSchema
>;

export const deleteMessageTemplateInput = z.object({ id: z.uuid() });
export type DeleteMessageTemplateInput = z.infer<
  typeof deleteMessageTemplateInput
>;

export const deleteMessageTemplateResultSchema = z.object({ id: z.uuid() });
export type DeleteMessageTemplateResult = z.infer<
  typeof deleteMessageTemplateResultSchema
>;

// Preview a message template. When `operationItemId` + `receiverId` are
// supplied the server renders the real Twilio variables for that
// item/operator; `notes` are the operator's in-flight edits so the preview
// reflects them live (same shape as `sendSupplier.notes`).
export const previewMessageTemplateInput = z.object({
  id: z.uuid(),
  operationItemId: z.uuid().optional(),
  receiverId: z.uuid().optional(),
  notes: messageTemplateNotesSchema.optional(),
});
export type PreviewMessageTemplateInput = z.infer<
  typeof previewMessageTemplateInput
>;

export const previewMessageTemplateResultSchema = z.object({
  renderedContent: z.string(),
  variables: z.record(z.string(), z.unknown()),
  // Legacy parity: true when a scheduled (CREATED) automated message already
  // exists for this (item, receiver, template) triple — the modal warns the
  // operator it will be repurposed when they send manually.
  hasScheduledMessage: z.boolean().optional(),
});
export type PreviewMessageTemplateResult = z.infer<
  typeof previewMessageTemplateResultSchema
>;
